import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import FlatButton from 'material-ui/FlatButton';
import MenuItem from 'material-ui/MenuItem';
import DatePicker from 'material-ui/DatePicker';
import RaisedButton from 'material-ui/RaisedButton';
import InfoSelectField from 'ntnu-react/material/InfoSelectField';
import InfoTextField from 'ntnu-react/material/InfoTextField';
import PersonPicker from 'ntnu-react/material/PersonPicker';
import { searchPerson } from '../utils';
import Label from 'ntnu-react/material/Label';
import HomeIcon from 'material-ui/svg-icons/action/home';

import status from '../constants/status.json';
import financing from '../constants/financing.json';
import changes from '../constants/changes.json';
import programs from '../constants/program.json';

const styles = {
    row: {
        marginTop: '2em',
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
    },
    flexLeft: {
        flex: 1,
        marginRight: 12,
    },
    flexRight: {
        flex: 1,
        marginLeft: 12,
    },
    flexBoth: {
        flex: 1,
        marginLeft: 12,
        marginRight: 12,
    },
    score: {
        flex: 1,
        textAlign: 'center',
        backgroundColor: '#fafafa',
        margin: 4,
        padding: 6,
    },
};

const Score = ({label, value}) => (
    <div style={styles.score}>
        <small style={{display: 'block'}}>{label}</small>
        {value}
    </div>
);
Score.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.string,
};

class ProcessProposal extends React.Component {
    handleSelectChange = (field, recalc) => {
        return (e, index, value) => {
            this.props.fieldChange(field, value, recalc);
        };
    }

    handleTextChange = (field, recalc) => {
        return (e) => {
            this.props.fieldChange(field, e.target.value, recalc);
        };
    }

    handleDateChange = (field, recalc) => {
        return (e, date) => {
            this.props.fieldChange(field, moment(date).format('DD/MM/YYYY'), recalc);
        };
    }

    handlePeopleChange = (field, recalc) => {
        return (name) => {
            this.props.fieldChange(field, name, recalc);
        };
    }

    handlePeopleSearch = (text) => {
        return searchPerson(text);
    }

    handleSave = () => this.props.saveProcessed();

    render() {
        const { data } = this.props;

        const otherFinancing = data.Financing1 === "4" && (
            <div style={styles.row}>
                <InfoTextField
                    floatingLabelText="Annen finansiering"
                    infoText="Beskriv annen finansiering"
                    floatingLabelFixed={true}
                    value={data.OtherFinancing}
                    fullWidth={true}
                    onChange={this.handleTextChange('OtherFinancing')}
                    multiLine={true}
                    rows={4}
                />
            </div>
        );

        return (
            <section>
                <Label style={{marginTop: '4rem'}}>Behandle søknad</Label>

                <div style={styles.row}>
                    <Score label="Totalt" value={data.AverageScore} />
                    <Score label="Strategi" value={data.StrategyAverage} />
                    <Score label="Nytte" value={data.BenefitAverage} />
                    <Score label="Omfang" value={data.ScopeAverage} />
                    <Score label="Risiko" value={data.RiskAverage} />
                    <Score label="Pålagt" value={data.Required1} />
                </div>

                <div style={styles.row}>
                    <InfoTextField
                        floatingLabelText="Fora behandlet"
                        infoText="Prosjektkontor, IT-styre, IT ledermøte ..."
                        floatingLabelFixed={true}
                        value={data.ProcessedForum}
                        fullWidth={true}
                        onChange={this.handleTextChange('ProcessedForum')}
                        wrapStyle={styles.flexLeft}
                    />

                    <DatePicker
                        floatingLabelText="Dato behandlet"
                        fullWidth={true}
                        value={data.ProcessedDate ? moment(data.ProcessedDate, 'DD/MM/YYYY').toDate(): new Date()}
                        onChange={this.handleDateChange('ProcessedDate')}
                        style={styles.flexBoth}
                        autoOk={true}
                    />

                    <InfoSelectField
                        floatingLabelText="Status"
                        floatingLabelFixed={true}
                        value={data.Status1}
                        fullWidth={true}
                        onChange={this.handleSelectChange('Status1')}
                        wrapStyle={styles.flexRight}
                    >
                        {status.map((cc) => (
                            <MenuItem
                                key={cc.key}
                                value={cc.key}
                                primaryText={cc.text}
                                insetChildren={true}
                                checked={data.Status1 === cc.key}
                            />
                        ))}
                    </InfoSelectField>
                </div>

                <div style={styles.row}>
                    <InfoTextField
                        floatingLabelText="Oppsummering av behandling"
                        infoText="Kort oppsummering av behandlingen"
                        floatingLabelFixed={true}
                        value={data.ProcessedSummary}
                        fullWidth={true}
                        onChange={this.handleTextChange('ProcessedSummary')}
                        multiLine={true}
                        rows={4}
                    />
                </div>

                <div style={styles.row}>
                    <InfoSelectField
                        floatingLabelText="Finansiering"
                        floatingLabelFixed={true}
                        value={data.Financing1}
                        onChange={this.handleSelectChange('Financing1')}
                    >
                        {financing.map((cc) => (
                            <MenuItem
                                key={cc.key}
                                value={cc.key}
                                primaryText={cc.text}
                                insetChildren={true}
                                checked={data.Financing1 === cc.key}
                            />
                        ))}
                    </InfoSelectField>
                </div>

                {otherFinancing}

                <div style={styles.row}>
                    <InfoSelectField
                        floatingLabelText="Endringsportefølje"
                        floatingLabelFixed={true}
                        fullWidth={true}
                        value={data.ChangePortfolio}
                        onChange={this.handleSelectChange('ChangePortfolio')}
                        wrapStyle={styles.flexLeft}
                    >
                        {changes.map((cc) => (
                            <MenuItem
                                key={cc.key}
                                value={cc.key}
                                primaryText={cc.text}
                                insetChildren={true}
                                checked={data.ChangePortfolio === cc.key}
                            />
                        ))}
                    </InfoSelectField>

                    <InfoSelectField
                        floatingLabelText="Forvaltningsområde"
                        floatingLabelFixed={true}
                        fullWidth={true}
                        value={data.AdminArea}
                        onChange={this.handleSelectChange('AdminArea')}
                        wrapStyle={styles.flexBoth}
                    >
                        {this.props.forvaltningOptions.map((cc) => (
                            <MenuItem
                                key={cc.key}
                                value={cc.key}
                                primaryText={cc.text}
                                insetChildren={true}
                                checked={data.AdminArea === cc.key}
                            />
                        ))}
                    </InfoSelectField>

                    <PersonPicker
                        floatingLabelText="Ansvarlig for oppfølging"
                        floatingLabelFixed={true}
                        value={data.Responsible}
                        onChange={this.handlePeopleChange('Responsible')}
                        fullWidth={true}
                        wrapStyle={styles.flexRight}
                        onSearch={this.handlePeopleSearch}
                    />
                </div>

                <div style={styles.row}>
                    <InfoTextField
                        floatingLabelText="URL til prosjektrom"
                        floatingLabelFixed={true}
                        placeholder="https://"
                        value={data.ProjectRoom}
                        fullWidth={true}
                        onChange={this.handleTextChange('ProjectRoom')}
                        wrapStyle={styles.flexLeft}
                    />

                    <DatePicker
                        floatingLabelText="Forslag oppstart"
                        fullWidth={true}
                        value={data.ProposedStart ? moment(data.ProposedStart, 'DD/MM/YYYY').toDate(): new Date()}
                        onChange={this.handleDateChange('ProposedStart')}
                        style={styles.flexRight}
                        autoOk={true}
                    />
                </div>

                <div style={Object.assign({alignItems: 'baseline'}, styles.row)}>
                    <InfoTextField
                        floatingLabelText="Prosjektportefølje nr"
                        floatingLabelFixed={true}
                        value={data.PortfolioNr}
                        fullWidth={true}
                        onChange={this.handleTextChange('PortfolioNr')}
                        wrapStyle={styles.flexLeft}
                    />

                    <div style={styles.flexRight}>
                        Sist endret av {data.ModifiedBy && data.ModifiedBy.text}
                    </div>
                </div>

                <div style={{marginTop: '1rem'}}>
                    <RaisedButton
                        label="Lagre"
                        primary={true}
                        onClick={this.handleSave}
                    />
                    <FlatButton label="Oversikt" icon={<HomeIcon/>} onClick={() => this.props.router.push('/')}/>
                </div>
            </section>
        );
    }
}

ProcessProposal.propTypes = {
    router: PropTypes.object.isRequired,
    data: PropTypes.object.isRequired,
    fieldChange: PropTypes.func.isRequired,
    saveProcessed: PropTypes.func.isRequired,
    forvaltningOptions: PropTypes.array.isRequired,
};

export default ProcessProposal;



// WEBPACK FOOTER //
// ./src/components/ProcessProposal.js